import styled, { CSSObject } from "@emotion/styled";
import { Locale } from "@kaltura/mediaspace-shared-types";
import { kmsCookie } from "@kaltura/mediaspace-shared-utils";
import { Button, buttonClasses, Menu, MenuItem, Tooltip, useMenu } from "@kaltura/ds-react-components";
import { ChevronDown16Icon, Check16Icon } from "@kaltura/ds-react-icons";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useHeaderTextStyle } from "../useHeaderTextStyle";

export interface LocaleMenuProps {
    locales: Locale[];
    onLocaleChange?: (locale: Locale) => void;
    currentLocale: Locale;
    disableScrollLock?: boolean;
    buttonColor?: "translucent" | "secondary";
}

const LocaleButton = styled(
    Button,
    {
        shouldForwardProp(propName: string) {
            return propName !== "headerTextStyle";
        },
    }
)<{ headerTextStyle: CSSObject }>(({ theme, headerTextStyle }) => ({
    backgroundColor: "initial",
    [`&:hover`]: {
        backgroundColor: "initial",
    },
    [`& .${buttonClasses.endIcon}`]: {
        margin: 0,
    },
    ...headerTextStyle,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        paddingLeft: theme.spacing(0.5), // - only on small screen
    },
}));

/**
 * Component to show locale selection
 */
export function LocaleMenu({
    locales,
    onLocaleChange,
    currentLocale,
    disableScrollLock = false,
    buttonColor = "translucent",
}: LocaleMenuProps) {
    const headerTextStyle = useHeaderTextStyle();

    const loadNewLocale = (locale: Locale) => {
        if (onLocaleChange) {
            onLocaleChange(locale);
            return;
        }
        createLocaleCookie(locale.id);
        // update url's lang param value according to selected language
        const currentUrl = window.location.href;
        const updatedUrl = currentUrl.replace(/(lang=)[^&]+/, "$1" + locale.id);
        // redirect to updated url
        // note: the following ESLint rule might be a false positive and might have been fixed in ESLint 10.x, see https://github.com/facebook/react/issues/35158, https://github.com/facebook/react/issues/34776
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = updatedUrl;
    };

    const createLocaleCookie = (localeId: string) => {
        const date = new Date();
        // set cookie for 6 months
        date.setTime(date.getTime() + 0.5 * 365 * 24 * 60 * 60 * 1000);

        const options = {
            expires: date.toUTCString(),
            path: "/",
        };

        kmsCookie("kms-locale", localeId, options);
    };

    const sendButtonAnalytics = useButtonAnalytics();

    const handleLocaleSelected = (localeId: string) => {
        const locale = locales.find((value) => value.id === localeId);
        // we are bound to find the value, because we got it from that list to start with.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        loadNewLocale(locale!);
        sendButtonAnalytics(`Language Selected - ${locale?.code}`, ButtonClickAnalyticsType.CHOOSE);
    };

    const { buttonProps, menuProps } = useMenu({
        menuId: "locale-menu",
        onOpen: () => sendButtonAnalytics("Language - Menu", ButtonClickAnalyticsType.MENU),
    });

    return (
        <div className={"kms-ds-locale-menu"}>
            <Tooltip title={translate("Select Language")} placement={"bottom-start"}>
                <LocaleButton
                    {...buttonProps}
                    color={buttonColor}
                    id="locale-button"
                    aria-label={translate("Select Language")}
                    endIcon={<ChevronDown16Icon />}
                    headerTextStyle={headerTextStyle}
                >
                    {currentLocale.code.toUpperCase()}
                </LocaleButton>
            </Tooltip>
            <Menu
                {...menuProps}
                slotProps={{
                    paper: {
                        style: {
                            width: "200px",
                        },
                    },
                    list: {
                        "aria-labelledby": "locale-button",
                    },
                }}
                disableScrollLock={disableScrollLock}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {locales.map((locale) => {
                    return (
                        <MenuItem
                            key={locale.id}
                            value={locale.id}
                            onClick={() => handleLocaleSelected(locale.id)}
                            selected={currentLocale.id === locale.id}
                            aria-current={currentLocale.id === locale.id}
                            icon={currentLocale.id === locale.id && <Check16Icon />}
                            lang={locale.code}
                        >
                            {locale.code.toUpperCase()} &nbsp;
                            {locale.clientName}
                        </MenuItem>
                    );
                })}
            </Menu>
        </div>
    );
}
